import { useSystemAssets } from '@bz/systemAssets';
import { cx } from '@bz/ui';
import { Pagination } from '@bz/ui';
import { createLazyMemo, createWritableMemo } from '@solid-primitives/memo';
import { Show, createEffect } from 'solid-js';
import { BetsList } from '@bz/betSlip/src/betslip/shared/components';
import { useTicketsData } from '@bz/betSlip/src/betslip/shared/hooks';
import { useCashoutPolling, useVisibilityObserver, useCashoutOverlay } from '@bz/betSlip/src/betslip/openBets/hook';
import { CashoutStatus } from '@bz/betSlip/src/betslip/openBets/hook/useCashoutPolling';
import { CashoutUpdatedOverlay } from '@bz/betSlip/src/betslip/openBets/CashoutUpdatedOverlay';

import { BetStatus, StatusFilter, statusFilters } from './components';

import createSearchParamSignal from '~/primitives/createSearchParamSignal';
import { useLayoutMainSticky, useSuspenseMinDelay } from '~/hook';
import { emitEvtBus, Events } from '~/hook/useEvtBus';
import useSubscribe from '~/primitives/useSubscribe';

export const route = {
  info: {
    login: true,
  },
  preload: () => {},
} satisfies RouteDefinition;

export function MyBetsContent({
  source: typeSlugParamSignal,
  isHomePage,
  hasLoadedOnce: hasLoadedOnceProp,
  onLoaded,
}: {
  source: any;
  isHomePage?: boolean;
  hasLoadedOnce?: () => boolean;
  onLoaded?: () => void;
}) {
  const filterValue = createLazyMemo(
    () => statusFilters.find((item: (typeof statusFilters)[number]) => item.slug === typeSlugParamSignal[0]())?.value
  );

  const [assets] = useSystemAssets();

  const [page, setPage] = createWritableMemo(
    on(filterValue, () => {
      return 1;
    })
  );

  // Use the shared useTicketsData hook
  const { ticketsData, isFirstLoading, isRefreshing, isMatchDetailsLoading, selectionsMap, refetch } = useTicketsData({
    settledStatus: () => {
      const value = filterValue(); // filterValue is a reactive accessor
      return value?.includes(BetStatus.ALL) === false ? value : undefined;
    },
    pageIndex: page, // Reactive page signal
    pageSize: 15,
    enablePolling: false,
  });

  const showListContent = useSuspenseMinDelay(() => !isFirstLoading() && !isRefreshing() && !isMatchDetailsLoading());

  // Notify parent when first load completes so the title and tab components persist across tab switches on the home page
  createEffect(() => {
    if (showListContent()) {
      onLoaded?.();
      emitEvtBus(Events.PENDING_DONE);
    }
  });

  // Shared refresh method that clears polling and re-checks visible elements
  const refreshTickets = async (...refetchArgs: Parameters<typeof refetch>) => {
    clearAllPolling();
    await refetch(...refetchArgs);
    // Re-check visible elements after the DOM updates
    requestAnimationFrame(() => {
      recheckVisibility();
    });
  };

  // Cashout polling: refresh the ticket list when a ticket is settled
  const { cashoutAmountMap, cashoutStatusMap, startPolling, stopPolling, clearAllPolling, isPolling } =
    useCashoutPolling({
      onTicketSettled: () => {
        // Ticket settled — refresh the list to remove it
        refreshTickets();
      },
    });

  const { containerRef, registerObserve, recheckVisibility } = useVisibilityObserver({
    onVisible: (ticketNo, time) => startPolling(ticketNo, time),
    onHidden: stopPolling,
  });

  // Store the outermost container DOM ref for scroll handling
  let layoutContainerRef: HTMLDivElement | undefined;

  // Cashout overlay logic
  const {
    overlayState,
    openCashoutOverlay,
    confirmCashout,
    closeCashoutOverlay,
    unavailableCashoutTickets,
    cashingTickets,
    removeUnavailableTicket,
  } = useCashoutOverlay({
    onSuccess: () => refreshTickets(false, true), // Silent refresh without skeleton
  });

  // Watch cashoutAmountMap — remove tickets from the unavailable set when their amount becomes valid again
  createEffect(() => {
    const amountMap = cashoutAmountMap();
    const unavailableSet = unavailableCashoutTickets();

    // Iterate the unavailable set and check whether any ticket amount has recovered
    unavailableSet.forEach((ticketNo) => {
      const amount = amountMap.get(ticketNo);
      // If the amount is valid (> 0 and finite), remove from the unavailable set
      if (amount !== undefined && amount > 0 && isFinite(amount)) {
        removeUnavailableTicket(ticketNo);
      }
    });
  });

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    // Scroll to the top of the layout
    layoutContainerRef?.scrollIntoView({ behavior: 'instant', block: 'start' });
  };

  useSubscribe(() => {
    const matchIds = ticketsData()?.betSelections.map((selection) => selection.matchId) ?? [];
    return Array.from(new Set(matchIds));
  });

  return (
    <div ref={layoutContainerRef}>
      <Show
        when={!isHomePage || hasLoadedOnceProp?.()}
        fallback={
          <div class="flex justify-center pt-4">
            <BarLoading class="size-14 !mb-[100vh]" />
          </div>
        }
      >
        <div class={cx('flex items-center gap-2 uppercase text-neutral-main1 heading-h6', !isHomePage && 'pt-6')}>
          <Show when={!isHomePage}>
            <IconMyBets class="size-8 text-neutral-main1_alt" />
          </Show>
          <span>{t('My Bets')}</span>
        </div>
        <div
          class={cx(
            'flex gap-2 p-1 rounded-full bg-tab-global-tab-bg mt-4',
            !isHomePage && 'sticky top-header-height z-20'
          )}
        >
          <StatusFilter selectedType={typeSlugParamSignal[0]()} onTypeChange={(type) => typeSlugParamSignal[1](type)} />
        </div>
        <Show
          when={showListContent()}
          fallback={
            <div class="mt-4 animate-in fade-in duration-300">
              <div class="flex justify-center pt-4">
                <BarLoading class="size-14 !mb-[100vh]" />
              </div>
            </div>
          }
        >
          <div
            ref={(el) => {
              containerRef(el);
            }}
            class="animate-in fade-in duration-300 mt-4"
          >
            <Show
              when={ticketsData()?.betTickets && ticketsData()!.betTickets.length > 0}
              fallback={
                <Transition.Scale start={0.8}>
                  <div class="w-full center min-h-96 py-24 gap-4 flex-col">
                    <img
                      style={{
                        width: '120px',
                        height: '120px',
                      }}
                      src={assets.mybetsemptyEmptyimg}
                    ></img>
                    <p class="text-xl font-semibold uppercase text-neutral-main1">{t('Nothing Here Yet')}</p>
                    <p class="text-neutral-main3 font-semibold text-sm -mt-2">
                      {t("It's empty now — start adding something.")}
                    </p>
                    <A href="/">
                      <Button class="h-10" type="second">
                        {t('Back Home')}
                      </Button>
                    </A>
                  </div>
                </Transition.Scale>
              }
            >
              <Transition.Scale start={0.9}>
                <div
                  class={cx('grid relative gap-4 min-h-[402px]')}
                  style={{
                    'grid-template-columns': `repeat(auto-fill, minmax(362px, 1fr))`,
                  }}
                >
                  <BetsList
                    tickets={ticketsData()!.betTickets}
                    selectionsMap={selectionsMap()}
                    enableCashout={true}
                    getCashoutAmount={(ticketNo: string) => cashoutAmountMap().get(ticketNo) ?? 0}
                    isCashoutHidden={(ticketNo: string) => {
                      const status = cashoutStatusMap().get(ticketNo);
                      return status === CashoutStatus.UNAVAILABLE || status === CashoutStatus.SETTLED;
                    }}
                    isCashoutUnavailable={(ticketNo: string) => {
                      // Show a disabled greyed-out button if the ticket is in the unavailable cashout set
                      return unavailableCashoutTickets().has(ticketNo);
                    }}
                    isCashingOut={(ticketNo: string) => {
                      // Show loading state if the ticket is currently being cashed out
                      return cashingTickets().has(ticketNo);
                    }}
                    onCashout={(ticket, amount) => openCashoutOverlay(ticket, amount)}
                    registerObserve={registerObserve}
                    containerClass="!m-0 min-h-full"
                    variant="mybets"
                    renderCashoutOverlay={(ticketNo) => (
                      <div class="absolute inset-0 overflow-hidden flex pointer-events-none flex-col z-50">
                        <CashoutUpdatedOverlay
                          isOpen={overlayState().isOpen && overlayState().ticketNo === ticketNo}
                          isLoadingAmount={overlayState().isLoading}
                          amount={overlayState().newAmount}
                          currencyCode={overlayState().currencyCode}
                          onConfirm={confirmCashout}
                          onCancel={closeCashoutOverlay}
                          onClose={closeCashoutOverlay}
                          class="h-full pt-24"
                        />
                      </div>
                    )}
                    class="min-h-[402px]"
                    isPolling={isPolling}
                    startPolling={startPolling}
                    stopPolling={stopPolling}
                  />
                </div>
              </Transition.Scale>

              <div class="mt-4 justify-center flex">
                <Pagination
                  page={page()}
                  total={ticketsData()!.total ?? 0}
                  pageSize={15}
                  onChange={handlePageChange}
                ></Pagination>
              </div>
            </Show>
          </div>
        </Show>
      </Show>
    </div>
  );
}

export default function () {
  const typeSlugParamSignal = createSearchParamSignal(
    'type',
    statusFilters[0].slug as (typeof statusFilters)[number]['slug']
  );

  useLayoutMainSticky({ onDeps: () => typeSlugParamSignal[0](), offsetTop: 72 });

  return (
    <Suspense>
      <ClientOnly>
        {/* Use For with a single-element array to force component remount */}
        {/* When typeSlugParamSignal[0]() changes, For destroys the old component and creates a new one */}
        <For each={[typeSlugParamSignal[0]()]}>{() => <MyBetsContent source={typeSlugParamSignal} />}</For>
      </ClientOnly>
    </Suspense>
  );
}
